import { callTemplateConfigurator } from './services/template-configurator.service';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import {
  createTemplateField,
  createTemplateScreen,
  deleteTemplateField,
  deleteTemplateScreen,
  getFieldsByScreenId,
  getScreensByTemplateId,
  updateTemplateScreen,
} from '@/modules/templates/services/templates.service';
import type {
  TemplateField,
  TemplateScreen,
  TemplateScreenUpdateInput,
  UdsTemplateField,
  UdsTemplateScreen,
} from '@/modules/templates/types/templates.types';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Eye,
  FileText,
  Loader2,
  Plus,
  RotateCcw,
  Send,
  Sparkles,
  Users,
  Wand2,
  X,
  Zap,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '@/hooks/use-toast';

// Types for the AI configurator
interface ScreenWithFields extends TemplateScreen {
  fields: TemplateField[];
}

// Condition types for conditional visibility
interface ConditionRule {
  field: string;
  operator: string;
  value?: unknown;
}

interface ConditionGroup {
  operator: 'and' | 'or';
  rules: ConditionRule[];
}

interface FieldData {
  name: string;
  label: string;
  type:
    | 'text'
    | 'email'
    | 'date'
    | 'number'
    | 'checkbox'
    | 'select'
    | 'multiselect'
    | 'textarea'
    | 'phone'
    | 'address'
    | 'party'
    | 'currency'
    | 'percentage'
    | 'url';
  required: boolean;
  placeholder?: string;
  helpText?: string;
  options?: string[];
  aiSuggestionEnabled?: boolean;
  aiSuggestionKey?: string;
  conditions?: ConditionGroup; // Conditional visibility
}

interface AIEnrichment {
  prompt: string;
  outputSchema: {
    type: 'object';
    properties: Record<string, { type: string; description?: string }>;
    required?: string[];
  };
}

interface PartyTypeConfig {
  value: string;
  label: string;
  description?: string;
}

interface SignatoryConfig {
  mode: 'deterministic' | 'dynamic';
  partyTypes: PartyTypeConfig[];
  minSignatories: number;
  maxSignatories: number;
  collectFields: {
    name: boolean;
    email: boolean;
    title: boolean;
    phone: boolean;
    company: boolean;
    address?: boolean;
  };
  predefinedSignatories?: Array<{
    id: string;
    partyType: string;
    label: string;
    required: boolean;
    order: number;
  }>;
}

interface ScreenData {
  title: string;
  description?: string;
  type?: 'standard' | 'signatory' | 'dynamic';
  // Conditional visibility - show/hide screen based on previous form responses
  conditions?: ConditionGroup;
  // For standard screens
  fields?: FieldData[];
  aiEnrichment?: AIEnrichment;
  enableApplyStandards?: boolean; // One-click auto-fill for AI suggestions
  // For signatory screens
  signatoryConfig?: SignatoryConfig;
  // For dynamic screens
  dynamicPrompt?: string;
  dynamicMaxFields?: number;
}

interface UpdateScreenData extends ScreenData {
  screenId: string; // ID of the screen to update
}

interface RemoveScreenData {
  screenId: string;
}

interface ReorderScreensData {
  screenOrder: string[]; // Array of screen IDs in new order
}

interface AIAction {
  type: 'createScreen' | 'updateScreen' | 'addFields' | 'removeScreen' | 'reorderScreens';
  data:
    | ScreenData
    | UpdateScreenData
    | FieldData
    | FieldData[]
    | RemoveScreenData
    | ReorderScreensData;
  applied?: boolean;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  actions?: AIAction[];
  suggestions?: string[];
  timestamp: Date;
  isError?: boolean;
  autoNext?: boolean;
}

interface AIConfiguratorProps {
  templateId: string;
  templateTitle: string;
  templateDescription: string;
  screens: ScreenWithFields[];
  selectedScreen: ScreenWithFields | null;
  onScreenCreated: () => void;
  onFieldsUpdated: () => void;
  onClose: () => void;
}

// Generate unique ID for messages
function generateId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

export function AIConfigurator({
  templateId,
  templateTitle,
  templateDescription,
  screens,
  selectedScreen,
  onScreenCreated,
  onFieldsUpdated,
  onClose,
}: AIConfiguratorProps) {
  const { toast } = useToast();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAutoApplying, setIsAutoApplying] = useState(false);
  const [contextExpanded, setContextExpanded] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const autoNextRef = useRef<boolean>(false);
  const applyActionDirectRef = useRef<((action: AIAction) => Promise<void>) | undefined>(undefined);
  const sendContinuationRef = useRef<(() => Promise<void>) | undefined>(undefined);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const sendMessage = useCallback(
    async (overrideMessage?: string) => {
      const messageText = overrideMessage ?? input;
      if (!messageText.trim() || isLoading) return;

      const userMessage: ChatMessage = {
        id: generateId(),
        role: 'user',
        content: messageText.trim(),
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setInput('');
      setIsLoading(true);

      try {
        const data = await callTemplateConfigurator({
          messages: [...messages, userMessage].map((m) => ({
            role: m.role as 'user' | 'assistant',
            content: m.content,
          })),
          templateContext: {
            templateId,
            templateTitle,
            templateDescription,
            screens: screens.map((s) => ({
              id: s.id,
              title: s.title,
              description: s.description,
              order: s.order,
              type: (s.type as 'standard' | 'signatory' | 'dynamic') || 'standard',
              enableApplyStandards: s.enableApplyStandards || false,
              aiPrompt: s.aiPrompt,
              aiOutputSchema: s.aiOutputSchema,
              signatoryConfig: s.signatoryConfig,
              dynamicPrompt: s.dynamicPrompt,
              dynamicMaxFields: s.dynamicMaxFields,
              conditions: s.conditions,
              fields: s.fields.map((f) => ({
                name: f.name,
                label: f.label,
                type: f.type,
                required: f.required,
                placeholder: f.placeholder,
                helpText: f.helpText,
                options: f.options,
                aiSuggestionEnabled: f.aiSuggestionEnabled || false,
                aiSuggestionKey: f.aiSuggestionKey,
                conditions: f.conditions,
              })),
            })),
          },
          selectedScreenId: selectedScreen?.id,
        });

        const assistantMessage: ChatMessage = {
          id: generateId(),
          role: 'assistant',
          content: data.message,
          actions: data.actions,
          suggestions: data.suggestions,
          timestamp: new Date(),
          autoNext: data.autoNext,
        };

        setMessages((prev) => [...prev, assistantMessage]);

        // Handle auto-apply and auto-continue
        if (data.autoNext && data.actions && data.actions.length > 0) {
          autoNextRef.current = true;
          setIsAutoApplying(true);

          // Auto-apply all actions sequentially
          for (let i = 0; i < data.actions.length; i++) {
            const action = data.actions[i];
            try {
              await applyActionDirectRef.current?.(action);
              // Mark action as applied in the message
              setMessages((prev) =>
                prev.map((msg) => {
                  if (msg.id === assistantMessage.id && msg.actions) {
                    const updatedActions = [...msg.actions];
                    updatedActions[i] = { ...action, applied: true };
                    return { ...msg, actions: updatedActions };
                  }
                  return msg;
                })
              );
            } catch (err) {
              console.error('Auto-apply failed:', err);
              toast({ variant: 'destructive', title: 'Error', description: 'Failed to auto-apply action' });
              setIsAutoApplying(false);
              autoNextRef.current = false;
              return;
            }
          }

          setIsAutoApplying(false);

          // Trigger screen refresh
          onScreenCreated();

          // Small delay to let the UI update, then auto-continue
          setTimeout(() => {
            if (autoNextRef.current) {
              autoNextRef.current = false;
              sendContinuationRef.current?.();
            }
          }, 500);
        }
      } catch (error) {
        console.error('Error sending message:', error);
        const errorMessage: ChatMessage = {
          id: generateId(),
          role: 'assistant',
          content: 'Sorry, I encountered an error processing your request. Please try again.',
          timestamp: new Date(),
          isError: true,
        };
        setMessages((prev) => [...prev, errorMessage]);
        toast({ variant: 'destructive', title: 'Error', description: 'Failed to get AI response' });
      } finally {
        setIsLoading(false);
      }
    },
    [
      input,
      isLoading,
      messages,
      templateId,
      templateTitle,
      templateDescription,
      screens,
      selectedScreen,
      onScreenCreated,
      toast,
    ]
  );

  // Helper function to apply action directly (for auto-apply)
  const applyActionDirect = useCallback(
    async (action: AIAction) => {
      if (action.type === 'createScreen') {
        const screenData = action.data as ScreenData;
        const screenType = screenData.type || 'standard';

        const screenPayload: Record<string, unknown> = {
          title: screenData.title,
          description: screenData.description || '',
          type: screenType,
        };

        if (screenData.enableApplyStandards !== undefined) {
          screenPayload.enableApplyStandards = screenData.enableApplyStandards;
        }

        if (screenData.conditions && screenData.conditions.rules?.length > 0) {
          screenPayload.conditions = JSON.stringify(screenData.conditions);
        }

        if (screenType === 'signatory' && screenData.signatoryConfig) {
          screenPayload.signatoryConfig = JSON.stringify(screenData.signatoryConfig);
        }
        if (screenType === 'dynamic') {
          if (screenData.dynamicPrompt) {
            screenPayload.dynamicPrompt = screenData.dynamicPrompt;
          }
          if (screenData.dynamicMaxFields) {
            screenPayload.dynamicMaxFields = screenData.dynamicMaxFields;
          }
        }

        const result = await createTemplateScreen({
          TemplateId: templateId,
          Title: screenPayload.title as string,
          Description: (screenPayload.description as string) || '',
          Type: screenType,
          Order: screens.length,
          EnableApplyStandards: screenPayload.enableApplyStandards as boolean | undefined,
          Conditions: screenPayload.conditions as string | undefined,
          SignatoryConfig: screenPayload.signatoryConfig as string | undefined,
          DynamicPrompt: screenPayload.dynamicPrompt as string | undefined,
          DynamicMaxFields: screenPayload.dynamicMaxFields as number | undefined,
        });
        const newScreen = { id: result?.insertTemplateScreens?.itemId };

        if (!newScreen.id) {
          throw new Error('Failed to create screen');
        }

        if (screenType === 'standard' && screenData.fields && screenData.fields.length > 0) {
          for (let fieldIdx = 0; fieldIdx < screenData.fields.length; fieldIdx++) {
            const field = screenData.fields[fieldIdx];
            await createTemplateField({
              ScreenId: newScreen.id,
              Name: field.name,
              Label: field.label,
              Type: field.type,
              Required: field.required,
              Placeholder: field.placeholder || '',
              HelpText: field.helpText || '',
              Options: field.options || [],
              Order: fieldIdx,
              AiSuggestionEnabled: field.aiSuggestionEnabled || false,
              AiSuggestionKey:
                field.aiSuggestionEnabled && field.aiSuggestionKey
                  ? field.aiSuggestionKey
                  : undefined,
              Conditions:
                field.conditions && field.conditions.rules?.length > 0
                  ? JSON.stringify(field.conditions)
                  : undefined,
            });
          }
        }

        if (screenData.aiEnrichment) {
          await updateTemplateScreen(newScreen.id, {
            AiPrompt: screenData.aiEnrichment.prompt,
            AiOutputSchema: JSON.stringify(screenData.aiEnrichment.outputSchema),
          });
        }

        let successMsg = `Screen "${screenData.title}" created`;
        if (screenType === 'standard') {
          successMsg += ` with ${screenData.fields?.length || 0} fields`;
        } else if (screenType === 'signatory') {
          successMsg += ' (signatory collection)';
        } else if (screenType === 'dynamic') {
          successMsg += ' (AI-generated fields)';
        }
        toast({ variant: 'success', title: 'Success', description: successMsg });
      } else if (action.type === 'updateScreen') {
        // Handle updateScreen for editing existing templates
        const updateData = action.data as UpdateScreenData;

        if (!updateData.screenId) {
          throw new Error('Missing screen ID for update');
        }

        // Update screen properties - build PascalCase input for UDS
        const screenUpdateInput: TemplateScreenUpdateInput = {};

        if (updateData.title) screenUpdateInput.Title = updateData.title;
        if (updateData.description !== undefined)
          screenUpdateInput.Description = updateData.description;
        if (updateData.enableApplyStandards !== undefined) {
          screenUpdateInput.EnableApplyStandards = updateData.enableApplyStandards;
        }
        if (updateData.conditions !== undefined) {
          screenUpdateInput.Conditions =
            updateData.conditions && updateData.conditions.rules?.length > 0
              ? JSON.stringify(updateData.conditions)
              : null;
        }
        if (updateData.aiEnrichment) {
          screenUpdateInput.AiPrompt = updateData.aiEnrichment.prompt;
          screenUpdateInput.AiOutputSchema = JSON.stringify(updateData.aiEnrichment.outputSchema);
        }
        if (updateData.type === 'signatory' && updateData.signatoryConfig) {
          screenUpdateInput.SignatoryConfig = JSON.stringify(updateData.signatoryConfig);
        }
        if (updateData.type === 'dynamic') {
          if (updateData.dynamicPrompt) screenUpdateInput.DynamicPrompt = updateData.dynamicPrompt;
          if (updateData.dynamicMaxFields)
            screenUpdateInput.DynamicMaxFields = updateData.dynamicMaxFields;
        }

        if (Object.keys(screenUpdateInput).length > 0) {
          await updateTemplateScreen(updateData.screenId, screenUpdateInput);
        }

        // If fields are provided, update them (delete existing and re-create)
        if (updateData.fields && updateData.fields.length > 0) {
          const existingScreen = screens.find((s) => s.id === updateData.screenId);
          if (existingScreen) {
            for (const field of existingScreen.fields) {
              await deleteTemplateField(field.id, true);
            }
          }

          for (let fieldIdx = 0; fieldIdx < updateData.fields.length; fieldIdx++) {
            const field = updateData.fields[fieldIdx];
            await createTemplateField({
              ScreenId: updateData.screenId,
              Name: field.name,
              Label: field.label,
              Type: field.type,
              Required: field.required,
              Placeholder: field.placeholder || '',
              HelpText: field.helpText || '',
              Options: field.options || [],
              Order: fieldIdx,
              AiSuggestionEnabled: field.aiSuggestionEnabled || false,
              AiSuggestionKey:
                field.aiSuggestionEnabled && field.aiSuggestionKey
                  ? field.aiSuggestionKey
                  : undefined,
              Conditions:
                field.conditions && field.conditions.rules?.length > 0
                  ? JSON.stringify(field.conditions)
                  : undefined,
            });
          }
        }

        const screenName = screens.find((s) => s.id === updateData.screenId)?.title || 'Screen';
        toast({ variant: 'success', title: 'Success', description: `Updated "${screenName}"` });
      }
    },
    [templateId, screens, toast]
  );
  applyActionDirectRef.current = applyActionDirect;

  // Send continuation message to get the next screen
  const sendContinuation = useCallback(async () => {
    setIsLoading(true);

    try {
      // Fetch the latest screens from UDS to ensure we have current data
      const screensResult = await getScreensByTemplateId(templateId);
      const rawScreens: UdsTemplateScreen[] = screensResult.items || [];

      // Fetch fields for each screen in parallel
      const fieldResults = await Promise.all(rawScreens.map((s) => getFieldsByScreenId(s.ItemId)));

      // Build ScreenWithFields[] in camelCase shape
      const latestScreens: ScreenWithFields[] = rawScreens
        .map((rawScreen, idx) => {
          const rawFields: UdsTemplateField[] = fieldResults[idx].items || [];
          return {
            id: rawScreen.ItemId,
            createdAt: rawScreen.CreatedDate,
            updatedAt: rawScreen.LastUpdatedDate,
            createdBy: rawScreen.CreatedBy,
            lastUpdatedBy: rawScreen.LastUpdatedBy,
            isDeleted: rawScreen.IsDeleted,
            organizationIds: rawScreen.OrganizationIds,
            tags: rawScreen.Tags,
            templateId: rawScreen.TemplateId,
            title: rawScreen.Title,
            description: rawScreen.Description,
            order: rawScreen.Order,
            type: rawScreen.Type,
            aiPrompt: rawScreen.AiPrompt,
            aiOutputSchema: rawScreen.AiOutputSchema,
            dynamicMaxFields: rawScreen.DynamicMaxFields,
            dynamicPrompt: rawScreen.DynamicPrompt,
            signatoryConfig: rawScreen.SignatoryConfig,
            uilmDescriptionKey: rawScreen.UilmDescriptionKey,
            uilmTitleKey: rawScreen.UilmTitleKey,
            enableApplyStandards: rawScreen.EnableApplyStandards,
            conditions: rawScreen.Conditions,
            fields: rawFields
              .map((f) => ({
                id: f.ItemId,
                createdAt: f.CreatedDate,
                updatedAt: f.LastUpdatedDate,
                createdBy: f.CreatedBy,
                lastUpdatedBy: f.LastUpdatedBy,
                isDeleted: f.IsDeleted,
                organizationIds: f.OrganizationIds,
                tags: f.Tags,
                screenId: f.ScreenId,
                name: f.Name,
                label: f.Label,
                type: f.Type,
                required: f.Required,
                placeholder: f.Placeholder,
                helpText: f.HelpText,
                options: f.Options,
                order: f.Order,
                aiSuggestionEnabled: f.AiSuggestionEnabled,
                aiSuggestionKey: f.AiSuggestionKey,
                uilmHelpTextKey: f.UilmHelpTextKey,
                uilmLabelKey: f.UilmLabelKey,
                uilmOptionsKeys: f.UilmOptionsKeys,
                uilmPlaceholderKey: f.UilmPlaceholderKey,
                conditions: f.Conditions,
              }))
              .sort((a, b) => a.order - b.order),
          };
        })
        .sort((a, b) => a.order - b.order);

      // if (process.env.NODE_ENV === "development") {
      //   console.log("[AI_CONFIGURATOR] Fetched latest screens for continuation:", latestScreens.length);
      // }

      // Get latest messages including the assistant's last message
      const currentMessages = [...messages];

      // Add a system continuation message
      const continuationMessage: ChatMessage = {
        id: generateId(),
        role: 'user',
        content: 'Continue with the next screen.',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, continuationMessage]);

      const data = await callTemplateConfigurator({
        messages: [...currentMessages, continuationMessage].map((m) => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
        })),
        templateContext: {
          templateId,
          templateTitle,
          templateDescription,
          screens: latestScreens.map((s: ScreenWithFields) => ({
            id: s.id,
            title: s.title,
            description: s.description,
            order: s.order,
            type: (s.type as 'standard' | 'signatory' | 'dynamic') || 'standard',
            enableApplyStandards: s.enableApplyStandards || false,
            aiPrompt: s.aiPrompt,
            aiOutputSchema: s.aiOutputSchema,
            signatoryConfig: s.signatoryConfig,
            dynamicPrompt: s.dynamicPrompt,
            dynamicMaxFields: s.dynamicMaxFields,
            conditions: s.conditions,
            fields: s.fields.map((f: TemplateField) => ({
              name: f.name,
              label: f.label,
              type: f.type,
              required: f.required,
              placeholder: f.placeholder,
              helpText: f.helpText,
              options: f.options,
              aiSuggestionEnabled: f.aiSuggestionEnabled || false,
              aiSuggestionKey: f.aiSuggestionKey,
              conditions: f.conditions,
            })),
          })),
        },
        selectedScreenId: selectedScreen?.id,
      });

      const assistantMessage: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: data.message,
        actions: data.actions,
        suggestions: data.suggestions,
        timestamp: new Date(),
        autoNext: data.autoNext,
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // Handle auto-apply and auto-continue for the continuation
      if (data.autoNext && data.actions && data.actions.length > 0) {
        autoNextRef.current = true;
        setIsAutoApplying(true);

        for (let i = 0; i < data.actions.length; i++) {
          const action = data.actions[i];
          try {
            await applyActionDirect(action);
            setMessages((prev) =>
              prev.map((msg) => {
                if (msg.id === assistantMessage.id && msg.actions) {
                  const updatedActions = [...msg.actions];
                  updatedActions[i] = { ...action, applied: true };
                  return { ...msg, actions: updatedActions };
                }
                return msg;
              })
            );
          } catch (err) {
            console.error('Auto-apply failed:', err);
            toast({ variant: 'destructive', title: 'Error', description: 'Failed to auto-apply action' });
            setIsAutoApplying(false);
            autoNextRef.current = false;
            return;
          }
        }

        setIsAutoApplying(false);
        onScreenCreated();

        setTimeout(() => {
          if (autoNextRef.current) {
            autoNextRef.current = false;
            sendContinuation();
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error in continuation:', error);
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to continue template creation' });
    } finally {
      setIsLoading(false);
    }
  }, [
    messages,
    templateId,
    templateTitle,
    templateDescription,
    selectedScreen,
    applyActionDirect,
    onScreenCreated,
    toast,
  ]);
  sendContinuationRef.current = sendContinuation;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    // Auto-send immediately when clicking a suggestion
    sendMessage(suggestion);
  };

  const applyAction = async (messageId: string, actionIndex: number, action: AIAction) => {
    try {
      if (action.type === 'createScreen') {
        const screenData = action.data as ScreenData;
        const screenType = screenData.type || 'standard';

        // Build the screen creation payload based on type
        const screenPayload: Record<string, unknown> = {
          title: screenData.title,
          description: screenData.description || '',
          type: screenType,
        };

        // Add enableApplyStandards if specified
        if (screenData.enableApplyStandards !== undefined) {
          screenPayload.enableApplyStandards = screenData.enableApplyStandards;
        }

        // Add conditions for conditional screen visibility
        if (screenData.conditions && screenData.conditions.rules?.length > 0) {
          screenPayload.conditions = JSON.stringify(screenData.conditions);
        }

        // Add type-specific configuration
        if (screenType === 'signatory' && screenData.signatoryConfig) {
          screenPayload.signatoryConfig = JSON.stringify(screenData.signatoryConfig);
        }
        if (screenType === 'dynamic') {
          if (screenData.dynamicPrompt) {
            screenPayload.dynamicPrompt = screenData.dynamicPrompt;
          }
          if (screenData.dynamicMaxFields) {
            screenPayload.dynamicMaxFields = screenData.dynamicMaxFields;
          }
        }

        // Create the screen via UDS
        const createResult = await createTemplateScreen({
          TemplateId: templateId,
          Title: screenPayload.title as string,
          Description: (screenPayload.description as string) || '',
          Type: screenType,
          Order: screens.length,
          EnableApplyStandards: screenPayload.enableApplyStandards as boolean | undefined,
          Conditions: screenPayload.conditions as string | undefined,
          SignatoryConfig: screenPayload.signatoryConfig as string | undefined,
          DynamicPrompt: screenPayload.dynamicPrompt as string | undefined,
          DynamicMaxFields: screenPayload.dynamicMaxFields as number | undefined,
        });
        const newScreen = { id: createResult?.insertTemplateScreens?.itemId };

        if (!newScreen.id) {
          throw new Error('Failed to create screen');
        }

        // Add fields to standard screens
        if (screenType === 'standard' && screenData.fields && screenData.fields.length > 0) {
          for (let fieldIdx = 0; fieldIdx < screenData.fields.length; fieldIdx++) {
            const field = screenData.fields[fieldIdx];
            await createTemplateField({
              ScreenId: newScreen.id,
              Name: field.name,
              Label: field.label,
              Type: field.type,
              Required: field.required,
              Placeholder: field.placeholder || '',
              HelpText: field.helpText || '',
              Options: field.options || [],
              Order: fieldIdx,
              AiSuggestionEnabled: field.aiSuggestionEnabled || false,
              AiSuggestionKey:
                field.aiSuggestionEnabled && field.aiSuggestionKey
                  ? field.aiSuggestionKey
                  : undefined,
              Conditions:
                field.conditions && field.conditions.rules?.length > 0
                  ? JSON.stringify(field.conditions)
                  : undefined,
            });
          }
        }

        // Save AI enrichment if provided (works for all screen types)
        if (screenData.aiEnrichment) {
          await updateTemplateScreen(newScreen.id, {
            AiPrompt: screenData.aiEnrichment.prompt,
            AiOutputSchema: JSON.stringify(screenData.aiEnrichment.outputSchema),
          });
        }

        // Mark action as applied
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === messageId && msg.actions) {
              const updatedActions = [...msg.actions];
              updatedActions[actionIndex] = { ...action, applied: true };
              return { ...msg, actions: updatedActions };
            }
            return msg;
          })
        );

        // Build success message based on screen type
        let successMsg = `Screen "${screenData.title}" created`;
        if (screenType === 'standard') {
          successMsg += ` with ${screenData.fields?.length || 0} fields`;
        } else if (screenType === 'signatory') {
          successMsg += ' (signatory collection)';
        } else if (screenType === 'dynamic') {
          successMsg += ' (AI-generated fields)';
        }
        if (screenData.aiEnrichment) {
          successMsg += ' + AI enrichment';
        }
        toast({ variant: 'success', title: 'Success', description: successMsg });
        onScreenCreated();
      } else if (action.type === 'addFields') {
        if (!selectedScreen) {
          toast({ variant: 'destructive', title: 'Error', description: 'Please select a screen to add fields to' });
          return;
        }

        const fieldsData = action.data as FieldData[];

        for (let fieldIdx = 0; fieldIdx < fieldsData.length; fieldIdx++) {
          const field = fieldsData[fieldIdx];
          await createTemplateField({
            ScreenId: selectedScreen.id,
            Name: field.name,
            Label: field.label,
            Type: field.type,
            Required: field.required,
            Placeholder: field.placeholder || '',
            HelpText: field.helpText || '',
            Options: field.options || [],
            Order: (selectedScreen.fields?.length || 0) + fieldIdx,
            AiSuggestionEnabled: field.aiSuggestionEnabled || false,
            AiSuggestionKey:
              field.aiSuggestionEnabled && field.aiSuggestionKey
                ? field.aiSuggestionKey
                : undefined,
            Conditions:
              field.conditions && field.conditions.rules?.length > 0
                ? JSON.stringify(field.conditions)
                : undefined,
          });
        }

        // Mark action as applied
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === messageId && msg.actions) {
              const updatedActions = [...msg.actions];
              updatedActions[actionIndex] = { ...action, applied: true };
              return { ...msg, actions: updatedActions };
            }
            return msg;
          })
        );

        toast({ variant: 'success', title: 'Success', description: `Added ${fieldsData.length} field(s) to "${selectedScreen.title}"` });
        onFieldsUpdated();
      } else if (action.type === 'updateScreen') {
        const updateData = action.data as UpdateScreenData;

        if (!updateData.screenId) {
          toast({ variant: 'destructive', title: 'Error', description: 'Missing screen ID for update' });
          return;
        }

        // First, update screen properties - build PascalCase input for UDS
        const screenUpdateInput2: TemplateScreenUpdateInput = {};

        if (updateData.title) screenUpdateInput2.Title = updateData.title;
        if (updateData.description !== undefined)
          screenUpdateInput2.Description = updateData.description;
        if (updateData.enableApplyStandards !== undefined) {
          screenUpdateInput2.EnableApplyStandards = updateData.enableApplyStandards;
        }
        // Add/update conditions for conditional screen visibility
        if (updateData.conditions !== undefined) {
          screenUpdateInput2.Conditions =
            updateData.conditions && updateData.conditions.rules?.length > 0
              ? JSON.stringify(updateData.conditions)
              : null; // Clear conditions if empty
        }
        if (updateData.aiEnrichment) {
          screenUpdateInput2.AiPrompt = updateData.aiEnrichment.prompt;
          screenUpdateInput2.AiOutputSchema = JSON.stringify(updateData.aiEnrichment.outputSchema);
        }
        if (updateData.type === 'signatory' && updateData.signatoryConfig) {
          screenUpdateInput2.SignatoryConfig = JSON.stringify(updateData.signatoryConfig);
        }
        if (updateData.type === 'dynamic') {
          if (updateData.dynamicPrompt) screenUpdateInput2.DynamicPrompt = updateData.dynamicPrompt;
          if (updateData.dynamicMaxFields)
            screenUpdateInput2.DynamicMaxFields = updateData.dynamicMaxFields;
        }

        // Update screen properties via UDS
        if (Object.keys(screenUpdateInput2).length > 0) {
          await updateTemplateScreen(updateData.screenId, screenUpdateInput2);
        }

        // If fields are provided, update them (delete existing and re-create)
        if (updateData.fields && updateData.fields.length > 0) {
          // Get existing fields to delete
          const existingScreen = screens.find((s) => s.id === updateData.screenId);
          if (existingScreen) {
            // Delete existing fields via UDS
            for (const field of existingScreen.fields) {
              await deleteTemplateField(field.id, true);
            }
          }

          // Create new fields via UDS
          for (let fieldIdx = 0; fieldIdx < updateData.fields.length; fieldIdx++) {
            const field = updateData.fields[fieldIdx];
            await createTemplateField({
              ScreenId: updateData.screenId,
              Name: field.name,
              Label: field.label,
              Type: field.type,
              Required: field.required,
              Placeholder: field.placeholder || '',
              HelpText: field.helpText || '',
              Options: field.options || [],
              Order: fieldIdx,
              AiSuggestionEnabled: field.aiSuggestionEnabled || false,
              AiSuggestionKey:
                field.aiSuggestionEnabled && field.aiSuggestionKey
                  ? field.aiSuggestionKey
                  : undefined,
              Conditions:
                field.conditions && field.conditions.rules?.length > 0
                  ? JSON.stringify(field.conditions)
                  : undefined,
            });
          }
        }

        // Mark action as applied
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === messageId && msg.actions) {
              const updatedActions = [...msg.actions];
              updatedActions[actionIndex] = { ...action, applied: true };
              return { ...msg, actions: updatedActions };
            }
            return msg;
          })
        );

        const screenName = screens.find((s) => s.id === updateData.screenId)?.title || 'Screen';
        toast({ variant: 'success', title: 'Success', description: `Updated "${screenName}" with AI improvements` });
        onScreenCreated(); // Refresh screens
      } else if (action.type === 'removeScreen') {
        const removeData = action.data as RemoveScreenData;

        if (!removeData.screenId) {
          toast({ variant: 'destructive', title: 'Error', description: 'Missing screen ID for removal' });
          return;
        }

        const screenName = screens.find((s) => s.id === removeData.screenId)?.title || 'Screen';

        await deleteTemplateScreen(removeData.screenId, true);

        // Mark action as applied
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === messageId && msg.actions) {
              const updatedActions = [...msg.actions];
              updatedActions[actionIndex] = { ...action, applied: true };
              return { ...msg, actions: updatedActions };
            }
            return msg;
          })
        );

        toast({ variant: 'success', title: 'Success', description: `Removed screen "${screenName}"` });
        onScreenCreated(); // Refresh screens
      } else if (action.type === 'reorderScreens') {
        const reorderData = action.data as ReorderScreensData;

        if (!reorderData.screenOrder || !Array.isArray(reorderData.screenOrder)) {
          toast({ variant: 'destructive', title: 'Error', description: 'Invalid screen order data' });
          return;
        }

        // Update order for each screen via UDS
        for (let i = 0; i < reorderData.screenOrder.length; i++) {
          const screenId = reorderData.screenOrder[i];
          await updateTemplateScreen(screenId, { Order: i });
        }

        // Mark action as applied
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === messageId && msg.actions) {
              const updatedActions = [...msg.actions];
              updatedActions[actionIndex] = { ...action, applied: true };
              return { ...msg, actions: updatedActions };
            }
            return msg;
          })
        );

        toast({ variant: 'success', title: 'Success', description: 'Screens reordered successfully' });
        onScreenCreated(); // Refresh screens
      }
    } catch (error) {
      console.error('Error applying action:', error);
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to apply changes' });
    }
  };

  const clearChat = () => {
    setMessages([]);
    setInput('');
  };

  return (
    <Card className="border-border h-full flex flex-col sticky top-4">
      <CardHeader className="pb-3 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-selise-blue/10 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-selise-blue" />
            </div>
            <div>
              <CardTitle className="text-base">AI Configurator</CardTitle>
              <CardDescription className="text-xs">
                Build screens with natural language
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={clearChat}
                title="Clear chat"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {/* Context Panel */}
      <div className="px-4 pb-2 shrink-0">
        <button
          type="button"
          onClick={() => setContextExpanded(!contextExpanded)}
          className="flex items-center gap-2 w-full text-left hover:opacity-70 transition-opacity"
        >
          {contextExpanded ? (
            <ChevronUp className="h-3 w-3 text-globe-grey" />
          ) : (
            <ChevronDown className="h-3 w-3 text-globe-grey" />
          )}
          <span className="text-xs font-medium text-globe-grey uppercase tracking-wider">
            Context
          </span>
        </button>
        {contextExpanded && (
          <div className="mt-2 p-3 bg-muted/30 rounded-lg border border-border">
            <div className="space-y-2 text-xs">
              <div>
                <span className="text-globe-grey">Template: </span>
                <span className="font-medium text-[hsl(var(--fg))]">{templateTitle}</span>
              </div>
              <div>
                <span className="text-globe-grey">Screens: </span>
                <span className="font-medium text-[hsl(var(--fg))]">{screens.length}</span>
              </div>
              {selectedScreen && (
                <div>
                  <span className="text-globe-grey">Selected: </span>
                  <Badge variant="secondary" className="text-xs py-0">
                    {selectedScreen.title}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Messages Area */}
      <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="h-16 w-16 rounded-2xl bg-selise-blue/10 flex items-center justify-center mb-4">
              <Sparkles className="h-8 w-8 text-selise-blue" />
            </div>
            <h3 className="font-semibold text-[hsl(var(--fg))] mb-2">
              Let&apos;s build your template
            </h3>
            <p className="text-sm text-globe-grey mb-4 max-w-70">
              Tell me what kind of template you&apos;re creating. I&apos;ll ask a few questions,
              then propose the complete structure with all screens.
            </p>
            <div className="space-y-2 w-full">
              <p className="text-xs text-globe-grey uppercase tracking-wider">Get started:</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {[
                  'I need an employment agreement',
                  'Help me create an NDA template',
                  'Build a service agreement',
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-selise-blue/5 hover:border-selise-blue/20 transition-colors text-[hsl(var(--fg))]"
                    onClick={() => {
                      setInput(suggestion);
                      inputRef.current?.focus();
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, messageIndex) => (
              <div key={message.id} className="space-y-2">
                <div
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[90%] rounded-2xl px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-selise-blue text-white'
                        : message.isError
                          ? 'bg-destructive/10 text-destructive'
                          : 'bg-muted text-[hsl(var(--fg))]'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>

                    {/* Actions */}
                    {message.actions && message.actions.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {message.actions.map((action, idx) => (
                          <ActionCard
                            key={idx}
                            action={action}
                            onApply={() => applyAction(message.id, idx, action)}
                            selectedScreen={selectedScreen}
                          />
                        ))}
                      </div>
                    )}

                    <p className="text-[10px] opacity-60 mt-2">
                      {message.timestamp.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>

                {/* Quick Reply Suggestions - show only on last assistant message */}
                {message.role === 'assistant' &&
                  message.suggestions &&
                  message.suggestions.length > 0 &&
                  messageIndex === messages.length - 1 &&
                  !isLoading && (
                    <div className="flex flex-wrap gap-2 pl-2">
                      {message.suggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="text-xs px-3 py-1.5 rounded-full border border-selise-blue/30 bg-selise-blue/5 hover:bg-selise-blue/10 hover:border-selise-blue/50 transition-colors text-selise-blue font-medium"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
              </div>
            ))}
            {(isLoading || isAutoApplying) && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-selise-blue" />
                    <span className="text-sm text-globe-grey">
                      {isAutoApplying ? 'Building screen...' : 'Thinking...'}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </CardContent>

      {/* Input Area */}
      <div className="p-4 border-t border-border shrink-0">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe the screen or fields you need..."
            className="flex-1 resize-none rounded-xl border border-input bg-transparent px-4 py-3 text-sm placeholder:text-globe-grey focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 min-h-12 max-h-30"
            rows={1}
            disabled={isLoading}
          />
          <Button
            onClick={() => sendMessage()}
            disabled={!input.trim() || isLoading}
            size="icon"
            className="h-12 w-12 rounded-xl shrink-0"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// Helper function to summarize conditions for display
function summarizeConditions(conditions: ConditionGroup): string {
  if (!conditions.rules || conditions.rules.length === 0) return '';

  const ruleDescriptions = conditions.rules.map((r) => {
    const fieldName = r.field;
    const op = r.operator;
    const val = r.value !== undefined ? String(r.value) : '';

    // Make operator more readable
    const opLabel: Record<string, string> = {
      equals: '=',
      notEquals: '≠',
      contains: 'contains',
      notContains: "doesn't contain",
      isEmpty: 'is empty',
      isNotEmpty: 'has value',
      greaterThan: '>',
      lessThan: '<',
      greaterThanOrEqual: '≥',
      lessThanOrEqual: '≤',
      in: 'is one of',
      notIn: 'is not',
      startsWith: 'starts with',
      endsWith: 'ends with',
    };

    const readableOp = opLabel[op] || op;

    if (op === 'isEmpty' || op === 'isNotEmpty') {
      return `${fieldName} ${readableOp}`;
    }
    return `${fieldName} ${readableOp} ${val}`;
  });

  const connector = conditions.operator === 'and' ? ' AND ' : ' OR ';
  return ruleDescriptions.join(connector);
}

// Action Card Component
interface ActionCardProps {
  action: AIAction;
  onApply: () => void;
  selectedScreen: ScreenWithFields | null;
}

function ActionCard({ action, onApply, selectedScreen }: ActionCardProps) {
  const [isApplying, setIsApplying] = useState(false);

  const handleApply = async () => {
    setIsApplying(true);
    try {
      await onApply();
    } finally {
      setIsApplying(false);
    }
  };

  if (action.type === 'createScreen') {
    const screenData = action.data as ScreenData;
    const screenType = screenData.type || 'standard';

    // Get icon and color based on screen type
    const getScreenIcon = () => {
      switch (screenType) {
        case 'signatory':
          return <Users className="h-4 w-4 text-mauveine mt-0.5 shrink-0" />;
        case 'dynamic':
          return <Zap className="h-4 w-4 text-lime-green mt-0.5 shrink-0" />;
        default:
          return <FileText className="h-4 w-4 text-selise-blue mt-0.5 shrink-0" />;
      }
    };

    const getScreenTypeLabel = () => {
      switch (screenType) {
        case 'signatory':
          return 'Signatory Screen';
        case 'dynamic':
          return 'Dynamic Screen';
        default:
          return 'Standard Screen';
      }
    };

    return (
      <div className="bg-white/80 rounded-xl p-3 border border-border">
        <div className="flex items-start gap-2 mb-2">
          {getScreenIcon()}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold text-[hsl(var(--fg))]">{screenData.title}</p>
              <Badge variant="outline" className="text-[9px] py-0 px-1.5">
                {getScreenTypeLabel()}
              </Badge>
            </div>
            {screenData.description && (
              <p className="text-xs text-globe-grey truncate">{screenData.description}</p>
            )}
          </div>
        </div>

        {/* Standard screen fields */}
        {screenType === 'standard' && screenData.fields && screenData.fields.length > 0 && (
          <div className="mb-3">
            <p className="text-[10px] text-globe-grey uppercase tracking-wider mb-1">
              Fields ({screenData.fields.length})
              {screenData.fields.some((f) => f.aiSuggestionEnabled) && (
                <span className="ml-1 text-selise-blue">
                  • {screenData.fields.filter((f) => f.aiSuggestionEnabled).length} with AI
                </span>
              )}
            </p>
            <div className="flex flex-wrap gap-1">
              {screenData.fields.map((field, idx) => (
                <Badge
                  key={idx}
                  variant="secondary"
                  className={`text-[10px] py-0 ${field.aiSuggestionEnabled ? 'bg-selise-blue/10 text-selise-blue' : ''}`}
                >
                  {field.aiSuggestionEnabled && <Sparkles className="h-2.5 w-2.5 mr-0.5" />}
                  {field.label}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Signatory screen config */}
        {screenType === 'signatory' && screenData.signatoryConfig && (
          <div className="mb-3 p-2 rounded-lg bg-[hsl(var(--mauveine))]/10 border border-[hsl(var(--mauveine))]/20">
            <div className="flex items-center gap-1.5 mb-1">
              <Users className="h-3 w-3 text-mauveine" />
              <span className="text-[10px] font-semibold text-mauveine uppercase tracking-wider">
                {screenData.signatoryConfig.mode === 'deterministic'
                  ? 'Fixed Signatories'
                  : 'Dynamic Collection'}
              </span>
            </div>
            <p className="text-[10px] text-[hsl(var(--fg))] mb-1">
              {screenData.signatoryConfig.minSignatories}-
              {screenData.signatoryConfig.maxSignatories} signatories
            </p>
            <div className="flex flex-wrap gap-1">
              {screenData.signatoryConfig.partyTypes.map((party) => (
                <Badge
                  key={party.value}
                  variant="outline"
                  className="text-[9px] py-0 px-1.5 bg-white/50 text-mauveine border-[hsl(var(--mauveine))]/30"
                >
                  {party.label}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Dynamic screen config */}
        {screenType === 'dynamic' && screenData.dynamicPrompt && (
          <div className="mb-3 p-2 rounded-lg bg-lime-green/10 border border-[hsl(var(--lime-green))]/20">
            <div className="flex items-center gap-1.5 mb-1">
              <Zap className="h-3 w-3 text-poly-green" />
              <span className="text-[10px] font-semibold text-poly-green uppercase tracking-wider">
                AI-Generated Fields
              </span>
            </div>
            <p className="text-[10px] text-[hsl(var(--fg))] line-clamp-2">
              {screenData.dynamicPrompt.substring(0, 80)}...
            </p>
            {screenData.dynamicMaxFields && (
              <p className="text-[9px] text-globe-grey mt-1">
                Max {screenData.dynamicMaxFields} fields
              </p>
            )}
          </div>
        )}

        {/* AI Enrichment (works for all screen types) */}
        {screenData.aiEnrichment && (
          <div className="mb-3 p-2 rounded-lg bg-selise-blue/10 border border-selise-blue/20">
            <div className="flex items-center gap-1.5 mb-1">
              <Wand2 className="h-3 w-3 text-selise-blue" />
              <span className="text-[10px] font-semibold text-selise-blue uppercase tracking-wider">
                AI Enrichment
              </span>
            </div>
            <p className="text-[10px] text-[hsl(var(--fg))] line-clamp-2 mb-1">
              {screenData.aiEnrichment.prompt.substring(0, 80)}...
            </p>
            <div className="flex flex-wrap gap-1">
              {Object.keys(screenData.aiEnrichment.outputSchema.properties || {}).map((key) => (
                <Badge
                  key={key}
                  variant="outline"
                  className="text-[9px] py-0 px-1.5 bg-white/50 text-selise-blue border-selise-blue/30"
                >
                  {key}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Apply Standards indicator */}
        {screenData.enableApplyStandards && (
          <div className="mb-3 flex items-center gap-2 p-2 rounded-lg bg-lime-green/10 border border-[hsl(var(--lime-green))]/20">
            <Zap className="h-3.5 w-3.5 text-poly-green" />
            <span className="text-[10px] font-medium text-poly-green">
              One-click &quot;Apply Standards&quot; enabled
            </span>
          </div>
        )}

        {/* Screen Conditions indicator */}
        {screenData.conditions &&
          screenData.conditions.rules &&
          screenData.conditions.rules.length > 0 && (
            <div className="mb-3 p-2 rounded-lg bg-[hsl(var(--globe-grey))]/10 border border-[hsl(var(--globe-grey))]/20">
              <div className="flex items-center gap-1.5 mb-1">
                <Eye className="h-3 w-3 text-globe-grey" />
                <span className="text-[10px] font-semibold text-globe-grey uppercase tracking-wider">
                  Conditional Screen
                </span>
              </div>
              <p className="text-[10px] text-[hsl(var(--fg))]">
                Shows when: {summarizeConditions(screenData.conditions)}
              </p>
            </div>
          )}

        {/* Conditional Fields indicator */}
        {screenType === 'standard' &&
          screenData.fields &&
          screenData.fields.some((f) => f.conditions && f.conditions.rules?.length > 0) && (
            <div className="mb-3 p-2 rounded-lg bg-[hsl(var(--globe-grey))]/5 border border-[hsl(var(--globe-grey))]/15">
              <div className="flex items-center gap-1.5 mb-1">
                <Eye className="h-3 w-3 text-globe-grey" />
                <span className="text-[10px] font-semibold text-globe-grey uppercase tracking-wider">
                  Conditional Fields (
                  {
                    screenData.fields.filter((f) => f.conditions && f.conditions.rules?.length > 0)
                      .length
                  }
                  )
                </span>
              </div>
              <div className="space-y-1">
                {screenData.fields
                  .filter((f) => f.conditions && f.conditions.rules?.length > 0)
                  .slice(0, 3)
                  .map((field, idx) => (
                    <p key={idx} className="text-[9px] text-globe-grey">
                      <span className="font-medium">{field.label}</span>:{' '}
                      {summarizeConditions(field.conditions as ConditionGroup)}
                    </p>
                  ))}
                {screenData.fields.filter((f) => f.conditions && f.conditions.rules?.length > 0)
                  .length > 3 && (
                  <p className="text-[9px] text-globe-grey italic">
                    +
                    {screenData.fields.filter((f) => f.conditions && f.conditions.rules?.length > 0)
                      .length - 3}{' '}
                    more...
                  </p>
                )}
              </div>
            </div>
          )}
        <Button
          size="sm"
          className="w-full h-8 text-xs"
          onClick={handleApply}
          disabled={action.applied || isApplying}
        >
          {action.applied ? (
            <>
              <Check className="h-3 w-3 mr-1" />
              Applied
            </>
          ) : isApplying ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <Plus className="h-3 w-3 mr-1" />
              Create Screen
            </>
          )}
        </Button>
      </div>
    );
  }

  if (action.type === 'addFields') {
    const fieldsData = action.data as FieldData[];
    const conditionalFields = fieldsData.filter(
      (f) => f.conditions && f.conditions.rules?.length > 0
    );

    return (
      <div className="bg-white/80 rounded-xl p-3 border border-border">
        <div className="flex items-start gap-2 mb-2">
          <Plus className="h-4 w-4 text-lime-green mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-[hsl(var(--fg))]">
              Add {fieldsData.length} Field{fieldsData.length !== 1 ? 's' : ''}
            </p>
            {selectedScreen ? (
              <p className="text-xs text-globe-grey">to &quot;{selectedScreen.title}&quot;</p>
            ) : (
              <p className="text-xs text-crimson">Select a screen first</p>
            )}
          </div>
        </div>
        <div className="mb-3">
          <div className="flex flex-wrap gap-1">
            {fieldsData.map((field, idx) => (
              <Badge
                key={idx}
                variant="secondary"
                className={`text-[10px] py-0 ${field.aiSuggestionEnabled ? 'bg-selise-blue/10 text-selise-blue' : ''} ${field.conditions && field.conditions.rules?.length > 0 ? 'border-[hsl(var(--globe-grey))]/30' : ''}`}
              >
                {field.conditions && field.conditions.rules?.length > 0 && (
                  <Eye className="h-2.5 w-2.5 mr-0.5 text-globe-grey" />
                )}
                {field.aiSuggestionEnabled && <Sparkles className="h-2.5 w-2.5 mr-0.5" />}
                {field.label}
              </Badge>
            ))}
          </div>
        </div>

        {/* Conditional Fields indicator */}
        {conditionalFields.length > 0 && (
          <div className="mb-3 p-2 rounded-lg bg-[hsl(var(--globe-grey))]/5 border border-[hsl(var(--globe-grey))]/15">
            <div className="flex items-center gap-1.5 mb-1">
              <Eye className="h-3 w-3 text-globe-grey" />
              <span className="text-[10px] font-semibold text-globe-grey uppercase tracking-wider">
                Conditional Fields ({conditionalFields.length})
              </span>
            </div>
            <div className="space-y-1">
              {conditionalFields.slice(0, 3).map((field, idx) => (
                <p key={idx} className="text-[9px] text-globe-grey">
                  <span className="font-medium">{field.label}</span>:{' '}
                  {summarizeConditions(field.conditions as ConditionGroup)}
                </p>
              ))}
              {conditionalFields.length > 3 && (
                <p className="text-[9px] text-globe-grey italic">
                  +{conditionalFields.length - 3} more...
                </p>
              )}
            </div>
          </div>
        )}
        <Button
          size="sm"
          className="w-full h-8 text-xs"
          onClick={handleApply}
          disabled={action.applied || isApplying || !selectedScreen}
        >
          {action.applied ? (
            <>
              <Check className="h-3 w-3 mr-1" />
              Applied
            </>
          ) : isApplying ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Adding...
            </>
          ) : (
            <>
              <Plus className="h-3 w-3 mr-1" />
              Add Fields
            </>
          )}
        </Button>
      </div>
    );
  }

  if (action.type === 'updateScreen') {
    const updateData = action.data as UpdateScreenData;
    const improvements: string[] = [];

    if (updateData.enableApplyStandards) improvements.push('Apply Standards');
    if (updateData.aiEnrichment) improvements.push('AI Enrichment');
    if (updateData.fields?.some((f) => f.aiSuggestionEnabled)) {
      const count = updateData.fields?.filter((f) => f.aiSuggestionEnabled).length || 0;
      improvements.push(`${count} AI-assisted fields`);
    }
    if (updateData.conditions && updateData.conditions.rules?.length > 0) {
      improvements.push('Conditional visibility');
    }
    if (updateData.fields?.some((f) => f.conditions && f.conditions.rules?.length > 0)) {
      const count =
        updateData.fields?.filter((f) => f.conditions && f.conditions.rules?.length > 0).length ||
        0;
      improvements.push(`${count} conditional fields`);
    }

    return (
      <div className="bg-linear-to-br from-[hsl(var(--selise-blue))]/5 to-[hsl(var(--selise-blue))]/10 rounded-xl p-3 border border-selise-blue/30">
        <div className="flex items-start gap-2 mb-2">
          <Wand2 className="h-4 w-4 text-selise-blue mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold text-[hsl(var(--fg))]">
                Update: {updateData.title || 'Screen'}
              </p>
              <Badge
                variant="outline"
                className="text-[9px] py-0 px-1.5 bg-selise-blue/10 text-selise-blue border-selise-blue/30"
              >
                AI Improvements
              </Badge>
            </div>
            {improvements.length > 0 && (
              <p className="text-[10px] text-selise-blue">Adding: {improvements.join(', ')}</p>
            )}
          </div>
        </div>

        {/* Screen Conditions indicator */}
        {updateData.conditions &&
          updateData.conditions.rules &&
          updateData.conditions.rules.length > 0 && (
            <div className="mb-3 p-2 rounded-lg bg-[hsl(var(--globe-grey))]/10 border border-[hsl(var(--globe-grey))]/20">
              <div className="flex items-center gap-1.5 mb-1">
                <Eye className="h-3 w-3 text-globe-grey" />
                <span className="text-[10px] font-semibold text-globe-grey uppercase tracking-wider">
                  Conditional Screen
                </span>
              </div>
              <p className="text-[10px] text-[hsl(var(--fg))]">
                Shows when: {summarizeConditions(updateData.conditions)}
              </p>
            </div>
          )}

        {updateData.fields && updateData.fields.length > 0 && (
          <div className="mb-3">
            <p className="text-[10px] text-globe-grey uppercase tracking-wider mb-1">
              Fields ({updateData.fields.length})
            </p>
            <div className="flex flex-wrap gap-1">
              {updateData.fields.slice(0, 8).map((field, idx) => (
                <Badge
                  key={idx}
                  variant="secondary"
                  className={`text-[10px] py-0 ${field.aiSuggestionEnabled ? 'bg-selise-blue/10 text-selise-blue' : ''}`}
                >
                  {field.aiSuggestionEnabled && <Sparkles className="h-2.5 w-2.5 mr-0.5" />}
                  {field.label}
                </Badge>
              ))}
              {updateData.fields.length > 8 && (
                <Badge variant="secondary" className="text-[10px] py-0">
                  +{updateData.fields.length - 8} more
                </Badge>
              )}
            </div>
          </div>
        )}

        <Button
          size="sm"
          className="w-full h-8 text-xs bg-selise-blue hover:bg-selise-blue/90"
          onClick={handleApply}
          disabled={action.applied || isApplying}
        >
          {action.applied ? (
            <>
              <Check className="h-3 w-3 mr-1" />
              Updated
            </>
          ) : isApplying ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <Wand2 className="h-3 w-3 mr-1" />
              Apply Improvements
            </>
          )}
        </Button>
      </div>
    );
  }

  if (action.type === 'removeScreen') {
    const removeData = action.data as RemoveScreenData;
    return (
      <div className="bg-crimson/5 rounded-xl p-3 border border-[hsl(var(--crimson))]/30">
        <div className="flex items-start gap-2 mb-2">
          <X className="h-4 w-4 text-crimson mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-[hsl(var(--fg))]">Remove Screen</p>
            <p className="text-[10px] text-crimson">ID: {removeData.screenId.substring(0, 8)}...</p>
          </div>
        </div>
        <Button
          size="sm"
          variant="destructive"
          className="w-full h-8 text-xs"
          onClick={handleApply}
          disabled={action.applied || isApplying}
        >
          {action.applied ? (
            <>
              <Check className="h-3 w-3 mr-1" />
              Removed
            </>
          ) : isApplying ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Removing...
            </>
          ) : (
            <>
              <X className="h-3 w-3 mr-1" />
              Remove
            </>
          )}
        </Button>
      </div>
    );
  }

  if (action.type === 'reorderScreens') {
    const reorderData = action.data as ReorderScreensData;
    return (
      <div className="bg-[hsl(var(--globe-grey))]/5 rounded-xl p-3 border border-[hsl(var(--globe-grey))]/30">
        <div className="flex items-start gap-2 mb-2">
          <FileText className="h-4 w-4 text-globe-grey mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-[hsl(var(--fg))]">Reorder Screens</p>
            <p className="text-[10px] text-globe-grey">
              {reorderData.screenOrder?.length || 0} screens will be reordered
            </p>
          </div>
        </div>
        <Button
          size="sm"
          className="w-full h-8 text-xs"
          onClick={handleApply}
          disabled={action.applied || isApplying}
        >
          {action.applied ? (
            <>
              <Check className="h-3 w-3 mr-1" />
              Reordered
            </>
          ) : isApplying ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Reordering...
            </>
          ) : (
            <>
              <FileText className="h-3 w-3 mr-1" />
              Apply Order
            </>
          )}
        </Button>
      </div>
    );
  }

  return null;
}
